import { action } from '@ember/object';
import { throttle } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import type { Args as ToolTipArgs } from 'webapp/components/tool-tip';
import ToolTipComponent from 'webapp/components/tool-tip';

interface CommonConditionalTooltipInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class CommonConditionalTooltipComponent extends Component<
	CommonConditionalTooltipInterface<ToolTipArgs>
> {
	@tracked includeTooltip = false;
	node: HTMLElement | null = null;
	resizeObserver: ResizeObserver | null = null;

	observerHandler = () => {
		throttle(this, this.checkOverlowState, 200, false);
	};

	bindObserver = () => {
		if (this.node) {
			this.resizeObserver = new ResizeObserver(this.observerHandler);
			this.resizeObserver.observe(this.node);
		}
	};

	unbindObserver = () => {
		if (this.node) {
			this.resizeObserver?.unobserve(this.node);
		}
	};

	@action
	setupView(node: HTMLElement) {
		this.unbindObserver();
		this.node = node;
		this.bindObserver();
		this.checkOverlowState();
	}

	@action
	checkOverlowState() {
		const node = this.node;
		if (node) {
			this.includeTooltip = node.offsetWidth < node.scrollWidth;
		}
	}

	override willDestroy(): void {
		super.willDestroy();
		this.unbindObserver();
	}
	<template>
		{{#if this.includeTooltip}}
			<ToolTipComponent @text={{@text}} @maxWidth={{@maxWidth}} />
		{{/if}}

		<div class="tooltip-on-overflow" {{didInsert this.setupView}}>
			{{yield}}
		</div>
	</template>
}
